import React, { useState } from 'react';
import {
  Box,
  ButtonBase,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Check';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { UserRole } from './types';

interface RoleSelectorProps {
  roles: UserRole[];
  activeRole: UserRole;
  onRoleChange: (role: UserRole) => void;
}

const useStyles = makeStyles(theme => ({
  trigger: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 6,
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.06)'
        : 'rgba(0,0,0,0.04)',
    '&:hover': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255,255,255,0.1)'
          : 'rgba(0,0,0,0.08)',
    },
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  roleName: {
    fontSize: 12,
    fontWeight: 500,
  },
  chevron: {
    fontSize: 16,
    opacity: 0.6,
  },
  menuPaper: {
    minWidth: 200,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 16px',
  },
  scope: {
    fontSize: 11,
    opacity: 0.5,
    marginLeft: 'auto',
  },
  check: {
    fontSize: 16,
    marginLeft: 8,
  },
}));

export function RoleSelector({
  roles,
  activeRole,
  onRoleChange,
}: RoleSelectorProps) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (role: UserRole, e: React.MouseEvent) => {
    e.stopPropagation();
    onRoleChange(role);
    setAnchorEl(null);
  };

  return (
    <>
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        <Typography variant="body2" style={{ fontSize: 11, opacity: 0.6 }}>
          Active role
        </Typography>
        <ButtonBase className={classes.trigger} onClick={handleOpen}>
          <span
            className={classes.dot}
            style={{ backgroundColor: activeRole.color }}
          />
          <Typography className={classes.roleName}>
            {activeRole.name}
          </Typography>
          <ExpandMoreIcon className={classes.chevron} />
        </ButtonBase>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: classes.menuPaper }}
      >
        {roles.map(role => (
          <MenuItem
            key={role.groupRef}
            className={classes.menuItem}
            onClick={e => handleSelect(role, e)}
          >
            <span
              className={classes.dot}
              style={{ backgroundColor: role.color }}
            />
            <Typography variant="body2">{role.name}</Typography>
            {role.groupRef === activeRole.groupRef && (
                <CheckIcon className={classes.check} />
              )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
